import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTreeStore } from '../stores/tree.js'
import { useAuthStore } from '../stores/auth.js'
import { api, mediaUrl } from '../services/api.js'
import { usePageTitle } from '../hooks/usePageTitle.js'
import { extractYear } from '../utils/date.js'
import OriginResearchHero from '../components/dashboard/OriginResearchHero.jsx'

// "Relive the past" — tiles of capsules that touch the user's family,
// sized up to 6 with an 'All worlds →' button to the tree's Worlds tab.
const RELIVE_THEME_GRADIENT = {
  war:       'linear-gradient(135deg, #3a2520 0%, #6b3c28 100%)',
  childhood: 'linear-gradient(135deg, #2d4a3e 0%, #5a7d65 100%)',
  work:      'linear-gradient(135deg, #2a2a2a 0%, #4a3a2a 100%)',
  migration: 'linear-gradient(135deg, #1e3a5f 0%, #3d5a7f 100%)',
  epidemic:  'linear-gradient(135deg, #2d2a3a 0%, #4a4560 100%)',
  flood:     'linear-gradient(135deg, #1a3a5a 0%, #3a6080 100%)',
  other:     'linear-gradient(135deg, #2a2a2a 0%, #4a4a4a 100%)',
}
function ReliveThePast({ trees, navigate }) {
  const [capsules, setCapsules] = useState(null)
  useEffect(() => {
    if (!trees || trees.length === 0) { setCapsules([]); return }
    api.get(`/trees/${trees[0].id}/capsules`)
      .then(c => setCapsules(c))
      .catch(() => setCapsules([]))
  }, [trees])
  if (!capsules || capsules.length === 0) return null
  const shown = capsules.slice(0, 6)
  return (
    <div className="dash-section dash-relive">
      <div className="dash-section__header">
        <div className="dash-section__title">Relive the past</div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/tree/${trees[0].id}?view=worlds`)}>
          All worlds →
        </button>
      </div>
      <div className="dash-relive__strip">
        {shown.map(c => (
          <ReliveTile
            key={c.id}
            capsule={c}
            onOpen={() => navigate(`/tree/${trees[0].id}?view=worlds`)}
          />
        ))}
      </div>
    </div>
  )
}

// Tile with graceful image fallback — <img> onError swaps to a themed
// gradient per theme so missing hero URLs look intentional, not broken.
function ReliveTile({ capsule, onOpen }) {
  const [imgOk, setImgOk] = useState(true)
  const gradient = RELIVE_THEME_GRADIENT[capsule.theme] || RELIVE_THEME_GRADIENT.other
  return (
    <button className="dash-relive__tile" onClick={onOpen}>
      {capsule.hero_image_url && imgOk ? (
        <img
          className="dash-relive__tile-bg"
          src={capsule.hero_image_url}
          alt=""
          aria-hidden
          loading="lazy"
          onError={() => setImgOk(false)}
        />
      ) : (
        <div
          className="dash-relive__tile-bg"
          style={{ background: gradient }}
          aria-hidden
        />
      )}
      <div className="dash-relive__tile-overlay" />
      <div className="dash-relive__tile-body">
        <div className="dash-relive__tile-title">{capsule.title}</div>
        <div className="dash-relive__tile-sub">
          {capsule.start_year}{capsule.end_year !== capsule.start_year && `–${capsule.end_year}`}
          {' · '}
          {capsule.members.length} {capsule.members.length === 1 ? 'relative' : 'relatives'}
        </div>
      </div>
    </button>
  )
}

// Compact to-do list version — a subtle panel with an icon + title + one-line
// body per action, stacked in a scrollable list. First 4 visible; a "Show
// more" reveals the rest. Far less pushy than the big green hero block.
const JOURNEY_ICON = {
  unlock_capsule:   '✨',
  link_entities:    '🔗',
  upload_document:  '📄',
  add_birth_place:  '📍',
  enter_world:      '🌍',
  start_tree:       '🌱',
}

function JourneyCard({ navigate }) {
  const [actions, setActions] = useState(null)
  const [showAll, setShowAll] = useState(false)
  useEffect(() => {
    api.get('/archive/journey').then(setActions).catch(() => setActions([]))
  }, [])
  if (!actions || actions.length === 0) return null

  const visible = showAll ? actions : actions.slice(0, 4)

  return (
    <div className="dash-journey">
      <div className="dash-journey__header">
        <div className="dash-journey__eyebrow">Your journey · {actions.length} suggested {actions.length === 1 ? 'step' : 'steps'}</div>
        <div className="dash-journey__lead">Small moves that bring your family into sharper focus.</div>
      </div>
      <ul className="dash-journey__list">
        {visible.map((a, i) => (
          <li key={i}>
            <button
              className="dash-journey__item"
              onClick={() => navigate(a.href)}
            >
              <span className="dash-journey__icon" aria-hidden>{JOURNEY_ICON[a.kind] || '·'}</span>
              <span className="dash-journey__text">
                <span className="dash-journey__title">{a.title}</span>
                <span className="dash-journey__body">{a.body}</span>
              </span>
              <span className="dash-journey__chev" aria-hidden>›</span>
            </button>
          </li>
        ))}
      </ul>
      {actions.length > 4 && (
        <button className="dash-journey__more" onClick={() => setShowAll(s => !s)}>
          {showAll ? 'Show fewer' : `Show ${actions.length - 4} more`}
        </button>
      )}
    </div>
  )
}

function CreateTreeModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ name: '', description: '' })
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    try {
      const tree = await api.post('/trees', form)
      onCreate(tree)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2 className="modal__title">New family tree</h2>
        <p className="modal__sub">Give your tree a name. You can always change it later.</p>
        <form onSubmit={handleSubmit}>
          <label>
            Tree name
            <input className="form-input" type="text" value={form.name}
              placeholder="e.g. De Vries family" autoFocus
              onChange={e => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label>
            Description <span style={{ textTransform: 'none', fontSize: '0.75rem', opacity: 0.6 }}>(optional)</span>
            <input className="form-input" type="text" value={form.description}
              placeholder="e.g. Rotterdam branch, 1800s–present"
              onChange={e => setForm({ ...form, description: e.target.value })} />
          </label>
          <div className="modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}
              style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create tree'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function MergeTreesModal({ trees, onClose, onMerged }) {
  // Combines two or more existing trees into a new "Tree C". Persons stay
  // global, so edits in Tree C flow back to the originals — the source
  // trees are kept as-is and can still be opened independently. The server
  // auto-merges unambiguous duplicates (same name + same birth year); any
  // remaining likely-duplicates are surfaced for manual review afterwards.
  const [selected, setSelected] = useState([])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  function toggle(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (selected.length < 2) { setError('Pick at least two trees to merge.'); return }
    if (!name.trim())        { setError('Give the merged tree a name.'); return }
    setLoading(true)
    try {
      const res = await api.post('/trees/merge', {
        source_tree_ids: selected,
        name: name.trim(),
        description: description.trim() || null,
      })
      onMerged(res)
    } catch (err) {
      setError(err.message || 'Merge failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '32rem' }}>
        <h2 className="modal__title">Merge trees</h2>
        <p className="modal__sub">
          Pick the trees to combine. We'll create a new tree with everyone from each, auto-merge
          obvious duplicates (same name + same birth year), and flag the rest for review.
          Your source trees are kept untouched.
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginTop: '1rem', maxHeight: '14rem', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: '0.5rem' }}>
            {trees.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>You don't have any trees yet.</div>}
            {trees.map(t => (
              <label key={t.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.25rem 0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selected.includes(t.id)}
                  onChange={() => toggle(t.id)}
                />
                <span>{t.name}</span>
                <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  {t.person_count || 0} persons
                </span>
              </label>
            ))}
          </div>
          <label style={{ marginTop: '1rem' }}>
            New tree name
            <input className="form-input" type="text" value={name}
              placeholder="e.g. Combined family tree" autoFocus
              onChange={e => setName(e.target.value)} required />
          </label>
          <label>
            Description <span style={{ textTransform: 'none', fontSize: '0.75rem', opacity: 0.6 }}>(optional)</span>
            <input className="form-input" type="text" value={description}
              placeholder="e.g. Father-side + mother-side combined"
              onChange={e => setDescription(e.target.value)} />
          </label>
          {error && <div style={{ color: '#b3261e', fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</div>}
          <div className="modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}
              style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || selected.length < 2}>
              {loading ? 'Merging…' : `Merge ${selected.length || ''} ${selected.length === 1 ? 'tree' : 'trees'}`.trim()}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function RenameTreeModal({ tree, onClose, onRenamed }) {
  const [name, setName] = useState(tree.name)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim()) return
    setLoading(true)
    try {
      const updated = await api.patch(`/trees/${tree.id}`, { name: name.trim() })
      onRenamed(updated)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h2 className="modal__title">Rename tree</h2>
        <form onSubmit={handleSubmit}>
          <label>
            Tree name
            <input className="form-input" type="text" value={name} autoFocus
              onChange={e => setName(e.target.value)} required />
          </label>
          <div className="modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}
              style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function TreeCardMenu({ tree, onRename, onDelete }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        className="tree-card-menu-btn"
        title="Options"
      >⋮</button>
      {open && (
        <div className="tree-card-menu">
          <button className="tree-card-menu__item" onClick={() => { setOpen(false); onRename() }}>
            Rename
          </button>
          <div className="tree-card-menu__divider" />
          <button className="tree-card-menu__item tree-card-menu__item--danger" onClick={() => { setOpen(false); onDelete() }}>
            Delete tree
          </button>
        </div>
      )}
    </div>
  )
}

export default function DashboardPage() {
  const { trees, fetchTrees, loading, focalPersonData, focalPersonId } = useTreeStore()
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [renamingTree, setRenamingTree] = useState(null)
  const [deletingTree, setDeletingTree] = useState(null)
  const [mysteryStats, setMysteryStats] = useState(null)

  usePageTitle('Home')
  useEffect(() => { fetchTrees() }, [])
  useEffect(() => {
    // Live counters on the Marketplace card. Best-effort — silent on
    // error so a stats outage doesn't blank the rest of the dashboard.
    api.get('/mysteries/stats').then(setMysteryStats).catch(() => {})
  }, [])

  function handleCreated(tree) {
    setShowModal(false)
    navigate(`/tree/${tree.id}`)
  }

  function handleMerged(result) {
    setShowMergeModal(false)
    fetchTrees()
    const s = result?.summary
    if (s) {
      const parts = [
        `${s.persons_copied} persons copied`,
        `${s.relationships_copied} relationships copied`,
        s.auto_merged > 0 ? `${s.auto_merged} duplicates auto-merged` : null,
        s.pending_duplicates > 0 ? `${s.pending_duplicates} likely duplicates flagged for review` : null,
      ].filter(Boolean)
      window.alert(`Merge complete.\n\n${parts.join('\n')}`)
    }
    if (result?.tree?.id) navigate(`/tree/${result.tree.id}`)
  }

  async function handleDelete(tree) {
    if (!window.confirm(`Delete "${tree.name}"? The tree will be removed. Persons shared with other trees are kept; persons only in this tree will be deleted.`)) return
    await api.delete(`/trees/${tree.id}`)
    fetchTrees()
  }

  const focal = focalPersonData
  const focalGiven = focal?.preferred_name || focal?.first_name || ''
  const focalSurname = focal?.last_name || ''
  const focalBirth = extractYear(focal?.birth_date)
  const focalDeath = extractYear(focal?.death_date)
  const focalYears = focalBirth ? (focalDeath ? `${focalBirth} – ${focalDeath}` : `b. ${focalBirth}`) : null
  const hasFocal = !!focal?.id

  const totalPersons = trees.reduce((s, t) => s + (Number(t.person_count) || 0), 0)
  const firstName = user?.name?.split(' ')[0] || 'back'

  return (
    <>
      <div className="dash">

        {/* ── Welcome banner — narrative framing for the product.
             "This is the story of your ancestors" lands first thing;
             the focal-person hero follows as "continue where you left off".
             A brand-new account sees only the banner. */}
        <div className="dash-welcome-narrative">
          <div className="dash-welcome-narrative__inner">
            <div className="dash-welcome-narrative__user">
              <div className="dash-welcome-narrative__avatar">
                {user?.self_photo_thumb_key
                  ? <img src={mediaUrl(user.self_photo_thumb_key)} alt={user?.name || ''} />
                  : user?.avatar_url
                    ? <img src={user.avatar_url} alt={user?.name || ''} />
                    : (user?.name?.[0]?.toUpperCase() || '?')}
              </div>
              <div>
                <div className="dash-welcome-narrative__hi">
                  {firstName === 'back' ? 'Welcome' : `Welcome back, ${firstName}`}
                </div>
                <div className="dash-welcome-narrative__email">{user?.email}</div>
              </div>
            </div>
            <h1 className="dash-welcome-narrative__title">
              This is the story of <em>your ancestors</em>.
            </h1>
            <p className="dash-welcome-narrative__lead">
              Join them in a journey to the past. The people, the streets,
              the years they lived through — waiting to be lived again.
            </p>
          </div>
        </div>

        {/* ── Onboarding nudge — only shown to users who skipped the
             post-signup wizard AND still haven't linked themselves to
             a person. Going through the wizard is the fastest path to
             a populated tree, so we keep nudging until they engage. */}
        {user && !user.onboarding_completed_at && user.onboarding_dismissed_at && !user.self_person_id && (
          <div className="dash-onboarding-banner">
            <div className="dash-onboarding-banner__body">
              <strong>Add your family</strong>
              <p>Start with you, your parents, and grandparents — it takes a minute.</p>
            </div>
            <button className="btn btn-gold" onClick={() => navigate('/onboarding')}>
              Open the wizard
            </button>
          </div>
        )}

        {/* ── Continue hero ── */}
        {hasFocal ? (
          <div className="dash-hero">
            <div className="dash-hero__bg" />
            <div className="dash-hero__content">
              <div className="dash-hero__eyebrow">Continue exploring</div>
              <div className="dash-hero__name">
                <span className="dash-hero__given">{focalGiven}</span>
                {focalSurname && <span className="dash-hero__surname">{focalSurname}</span>}
              </div>
              {focalYears && <div className="dash-hero__years">{focalYears}</div>}
              {focal.treeName && (
                <div className="dash-hero__tree">{focal.treeName}</div>
              )}
              <div className="dash-hero__actions">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/tree/${focal.treeId}/person/${focal.id}`)}
                >
                  View person
                </button>
                {focal.treeId && (
                  <button
                    className="btn btn-ghost"
                    style={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.15)' }}
                    onClick={() => navigate(`/tree/${focal.treeId}`)}
                  >
                    Open tree
                  </button>
                )}
              </div>
            </div>

            {focal.photo_thumb_key && (
              <div className="dash-hero__photo">
                <img
                  src={mediaUrl(focal.photo_thumb_key)}
                  alt={focalGiven}
                />
              </div>
            )}
          </div>
        ) : null}

        <OriginResearchHero />

        {/* JourneyCard is gone — the full My Journey page + sidebar badge
            make this block redundant on the dashboard. */}

        {/* ── Stats strip ── */}
        {!loading && trees.length > 0 && (
          <div className="dash-stats">
            <div className="dash-stat">
              <div className="dash-stat__value">{trees.length}</div>
              <div className="dash-stat__label">{trees.length === 1 ? 'Tree' : 'Trees'}</div>
            </div>
            <div className="dash-stat__sep" />
            <div className="dash-stat">
              <div className="dash-stat__value">{totalPersons}</div>
              <div className="dash-stat__label">{totalPersons === 1 ? 'Person' : 'Persons'}</div>
            </div>
            <div className="dash-stat__sep" />
            <div className="dash-stat dash-stat--muted">
              <div className="dash-stat__value">–</div>
              <div className="dash-stat__label">Documents</div>
            </div>
            <div className="dash-stat__sep" />
            <div className="dash-stat dash-stat--muted">
              <div className="dash-stat__value">–</div>
              <div className="dash-stat__label">Archive matches</div>
            </div>
          </div>
        )}

        <ReliveThePast trees={trees} navigate={navigate} />

        {/* ── Trees section ── */}
        <div className="dash-section">
          <div className="dash-section__header">
            <div className="dash-section__title">Family Trees</div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
              + New tree
            </button>
          </div>

          {loading && <div className="loading">Loading your trees</div>}

          {!loading && trees.length === 0 && (
            <div className="dash-empty">
              <div className="dash-empty__icon">🌳</div>
              <h3>Start your first tree</h3>
              <p>Create a family tree and add your first ancestor. The history they lived through will follow.</p>
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>Create tree</button>
            </div>
          )}

          {!loading && trees.length > 0 && (
            <div className="dash-trees">
              {trees.map(tree => (
                <div key={tree.id} className="dash-tree-card" onClick={() => navigate(`/tree/${tree.id}`)}>
                  <div className="dash-tree-card__accent" />
                  <div className="dash-tree-card__body">
                    <div className="dash-tree-card__name">{tree.name}</div>
                    {tree.description && (
                      <div className="dash-tree-card__desc">{tree.description}</div>
                    )}
                    <div className="dash-tree-card__meta">
                      <span>{tree.person_count || 0} persons</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <TreeCardMenu
                      tree={tree}
                      onRename={() => setRenamingTree(tree)}
                      onDelete={() => handleDelete(tree)}
                    />
                    <div className="dash-tree-card__arrow">→</div>
                  </div>
                </div>
              ))}

              <div className="dash-tree-card dash-tree-card--new" onClick={() => setShowModal(true)}>
                <span className="dash-tree-card__plus">+</span>
                <span>New tree</span>
              </div>

              {trees.length >= 2 && (
                <div className="dash-tree-card dash-tree-card--new" onClick={() => setShowMergeModal(true)}>
                  <span className="dash-tree-card__plus">⤳</span>
                  <span>Merge trees</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Research panels ── */}
        <div className="dash-section">
          <div className="dash-section__header">
            <div className="dash-section__title">Research</div>
          </div>
          <div className="dash-coming">
            <button
              className="dash-coming__card dash-coming__card--active"
              onClick={() => navigate('/archive')}
            >
              <div className="dash-coming__icon">🗄️</div>
              <div className="dash-coming__label">The Archive</div>
              <div className="dash-coming__sub">The shared knowledge graph — persons, places, eras</div>
              <div className="dash-coming__badge dash-coming__badge--live">Open →</div>
            </button>
            <button
              className="dash-coming__card dash-coming__card--active"
              onClick={() => trees[0] && navigate(`/tree/${trees[0].id}?view=library`)}
              disabled={!trees[0]}
            >
              <div className="dash-coming__icon">📚</div>
              <div className="dash-coming__label">Knowledge library</div>
              <div className="dash-coming__sub">Persons found in your documents</div>
              <div className="dash-coming__badge dash-coming__badge--live">Open →</div>
            </button>
            <button
              className="dash-coming__card dash-coming__card--active"
              onClick={() => trees[0] && navigate(`/tree/${trees[0].id}?view=places`)}
              disabled={!trees[0]}
            >
              <div className="dash-coming__icon">🗺️</div>
              <div className="dash-coming__label">Family geography</div>
              <div className="dash-coming__sub">Where your family has lived over time</div>
              <div className="dash-coming__badge dash-coming__badge--live">Open →</div>
            </button>
            <button
              className="dash-coming__card dash-coming__card--active dash-coming__card--feature"
              onClick={() => navigate('/mysteries')}
            >
              <div className="dash-coming__feature-tag">New · Earn cash</div>
              <div className="dash-coming__icon">🔎</div>
              <div className="dash-coming__label">Mysteries Marketplace</div>
              <div className="dash-coming__sub">Solve other researchers' open leads for cash bounties — or post one on the gaps you can't crack yourself.</div>
              {mysteryStats && (mysteryStats.open_bounties > 0 || mysteryStats.marketplace_mysteries > 0) && (
                <div className="dash-coming__feature-stats">
                  {mysteryStats.marketplace_mysteries > 0 && (
                    <span><strong>{mysteryStats.marketplace_mysteries}</strong> open {mysteryStats.marketplace_mysteries === 1 ? 'mystery' : 'mysteries'}</span>
                  )}
                  {mysteryStats.open_bounties > 0 && (
                    <span><strong>{mysteryStats.open_bounties}</strong> bounty {mysteryStats.open_bounties === 1 ? '' : 'bounties'} · €{Math.round(Number(mysteryStats.escrow_cents || 0) / 100)} in escrow</span>
                  )}
                  {mysteryStats.solvers_paid_total > 0 && (
                    <span><strong>{mysteryStats.solvers_paid_total}</strong> researcher{mysteryStats.solvers_paid_total === 1 ? '' : 's'} paid</span>
                  )}
                </div>
              )}
              <div className="dash-coming__badge dash-coming__badge--feature">Browse & earn →</div>
            </button>
          </div>
        </div>

      </div>

      {showModal && <CreateTreeModal onClose={() => setShowModal(false)} onCreate={handleCreated} />}
      {showMergeModal && (
        <MergeTreesModal
          trees={trees}
          onClose={() => setShowMergeModal(false)}
          onMerged={handleMerged}
        />
      )}
      {renamingTree && (
        <RenameTreeModal
          tree={renamingTree}
          onClose={() => setRenamingTree(null)}
          onRenamed={() => { setRenamingTree(null); fetchTrees() }}
        />
      )}
    </>
  )
}
